import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, AlertTriangle, FileText } from 'lucide-react';

function PacketCard({ entry, tone = 'emerald' }) {
  const toneMap = {
    emerald: { badge: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40', ring: 'border-emerald-500/30' },
    cyan: { badge: 'text-cyan-300 bg-cyan-500/15 border-cyan-500/40', ring: 'border-cyan-500/30' },
  };
  const t = toneMap[tone] || toneMap.emerald;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`bg-[#0f0f16] border ${t.ring} rounded-xl p-5`}
      data-testid={`packet-card-${entry.packet_id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`text-[9px] font-bold tracking-[1.5px] border px-2 py-0.5 rounded ${t.badge}`}>
              {entry.network_label || 'EXECUTED'}
            </span>
            <span className="text-[10px] text-slate-500 font-mono">{entry.packet_id}</span>
            <span className="text-[10px] text-slate-500">·</span>
            <span className="text-[10px] text-slate-500">block {entry.block_number?.toLocaleString?.() || entry.block_number}</span>
          </div>
          <div className="text-white font-semibold truncate">
            Agent #{entry.agent_id}
          </div>
          <div className="text-sm text-slate-500 truncate">
            harmed party: <span className="text-slate-400 font-mono text-xs">{entry.harmed_party_display || entry.harmed_party_wallet}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold tracking-[1.5px] text-slate-400 mb-1">RECOURSE PAID</div>
          <div className="text-2xl font-black tabular-nums text-white">
            ${entry.debt_usd || '—'}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 tabular-nums">
            {entry.slash_amount} {entry.slash_amount_token_symbol || 'TOKEN'}
          </div>
        </div>
      </div>
      {entry.collateral_token_disclaimer && (
        <div className="mb-3 text-[11px] leading-relaxed text-amber-300/80 bg-amber-500/[0.06] border border-amber-500/20 rounded px-3 py-2">
          <strong className="text-amber-200">Collateral token note:</strong> {entry.collateral_token_disclaimer}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
        <a href={entry.packet_url} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5" data-testid={`view-packet-${entry.packet_id}`}>
            <FileText className="w-3.5 h-3.5 mr-2" />
            View JSON packet
          </Button>
        </a>
        <a href={entry.tx_explorer_url} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5" data-testid={`view-tx-${entry.packet_id}`}>
            <ExternalLink className="w-3.5 h-3.5 mr-2" />
            Verify on explorer
          </Button>
        </a>
      </div>
    </motion.div>
  );
}


export default function Proofs() {
  const [manifest, setManifest] = useState(null);

  useEffect(() => {
    fetch('/proofs/manifest.json')
      .then((r) => r.json())
      .then(setManifest)
      .catch(() => setManifest({ mainnet_slashes: [], sepolia_slashes: [], examples: [] }));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Slash Proofs · Kojiru</title>
        <meta name="description" content="Every real recourse execution the KojiruACSHook contract has fired, published as independently verifiable JSON. Honest empty state today; permanent public ledger going forward." />
      </Helmet>

      <Navbar />

      {/* Deprecation banner — old KOJI-bound Hook (sits below the fixed nav) */}
      <div className="mt-14 sm:mt-16 border-y border-amber-500/40 bg-amber-500/[0.15] border-l-4 border-l-amber-500" data-testid="deprecation-banner">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed text-amber-100">
            <strong className="text-amber-200">Contract deprecated.</strong> The original KOJI-bound Hook (<code className="text-amber-300 font-mono">0x16C7…4476</code>) has been retired. The current mainnet Hook is the USDC-bound <code className="text-amber-300 font-mono">0x05F2…0874</code> — this page reflects the new deployment. <a href="/proofs?v=15" className="text-amber-200 underline hover:text-white">See /proofs for live packets.</a>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative pt-10 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-6">
              PUBLIC LEDGER &middot; SLASH EXECUTIONS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Every real slash. Independently verifiable.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl">
              This page lists every recourse execution the KojiruACSHook contract has ever fired.
              Follow any transaction hash into a block explorer and confirm what happened yourself.
              No login. No permission. No trust required.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Honest status */}
      {(() => {
        const mainnetCount = manifest?.mainnet_slashes?.length || 0;
        const sepoliaCount = manifest?.sepolia_slashes?.length || 0;
        if (mainnetCount === 0 && sepoliaCount === 0) {
          return (
            <section className="py-10 px-6 border-b border-white/5 bg-amber-500/[0.04]">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-[10px] font-bold tracking-[2px] text-amber-300 mb-2">HONEST STATUS</div>
                    <h2 className="text-xl font-bold text-white mb-3">No real slashes have fired yet.</h2>
                    <p className="text-slate-300 leading-relaxed mb-3">
                      We would rather show you an empty page than a manufactured one.
                    </p>
                    <p className="text-slate-400 leading-relaxed italic mt-6 text-sm">
                      The illustrative packet below shows the structure a real entry will produce.
                      This page will update automatically the day the first real slash fires.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          );
        }
        if (mainnetCount === 0 && sepoliaCount > 0) {
          return (
            <section className="py-10 px-6 border-b border-white/5 bg-cyan-500/[0.04]">
              <div className="max-w-4xl mx-auto flex items-start gap-4">
                <AlertTriangle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                <div>
                  <div className="text-[10px] font-bold tracking-[2px] text-cyan-300 mb-2">HONEST STATUS</div>
                  <h2 className="text-lg font-bold text-white mb-2">Live on Base Sepolia · not yet on mainnet.</h2>
                  <p className="text-slate-300 leading-relaxed text-sm">
                    {sepoliaCount} real slash{sepoliaCount === 1 ? ' has' : 'es have'} fired on the Base Sepolia
                    test network. Every entry links to a real transaction hash you can verify yourself. Mainnet
                    remains empty until the first production agent posts real USDC collateral.
                  </p>
                </div>
              </div>
            </section>
          );
        }
        return null;
      })()}

      {/* Mainnet section */}
      <section className="py-10 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold tracking-[2px] text-emerald-400 mb-1">BASE MAINNET</div>
              <h2 className="text-xl font-black">Production slashes</h2>
            </div>
            <div className="text-sm text-slate-500">
              {manifest?.mainnet_slashes?.length || 0} execution{(manifest?.mainnet_slashes?.length || 0) === 1 ? '' : 's'}
            </div>
          </div>
          {manifest?.mainnet_slashes?.length ? (
            <div className="space-y-3">
              {manifest.mainnet_slashes.map((s) => (
                <PacketCard key={s.packet_id} entry={s} tone="emerald" />
              ))}
            </div>
          ) : (
            manifest && (
              <div className="text-sm text-slate-500 py-4 italic" data-testid="mainnet-empty">
                None yet.
              </div>
            )
          )}
        </div>
      </section>

      {/* Sepolia section */}
      <section className="py-10 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold tracking-[2px] text-cyan-400 mb-1">BASE SEPOLIA &middot; TEST NETWORK</div>
              <h2 className="text-xl font-black">Test-network executions</h2>
            </div>
            <div className="text-sm text-slate-500">
              {manifest?.sepolia_slashes?.length || 0} execution{(manifest?.sepolia_slashes?.length || 0) === 1 ? '' : 's'}
            </div>
          </div>
          {manifest?.sepolia_slashes?.length ? (
            <div className="space-y-3">
              {manifest.sepolia_slashes.map((s) => (
                <PacketCard key={s.packet_id} entry={s} tone="cyan" />
              ))}
            </div>
          ) : (
            manifest && (
              <div className="text-sm text-slate-500 py-4 italic" data-testid="sepolia-empty">
                None yet.
              </div>
            )
          )}
        </div>
      </section>

      {/* Illustrative example — only shown when there are no real packets yet */}
      {manifest && (manifest.mainnet_slashes?.length || 0) + (manifest.sepolia_slashes?.length || 0) === 0 && (
      <section className="py-12 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] font-bold tracking-[2px] text-slate-400 mb-1">ILLUSTRATIVE EXAMPLE</div>
              <h2 className="text-xl font-black">What a real entry will look like</h2>
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed mb-6">
            The packet below is not a real execution. It is the exact structure and field set that a real slash
            will produce once end-to-end execution is live. The numbers, hashes, and addresses are placeholders;
            everything else is the real schema.
          </p>
          {manifest?.examples?.map((ex) => (
            <motion.div
              key={ex.packet_id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#0f0f16] border border-slate-500/30 border-dashed rounded-xl p-6"
              data-testid={`illustrative-card-${ex.packet_id}`}
            >
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[9px] font-bold tracking-[1.5px] text-slate-300 bg-slate-500/20 border border-slate-500/40 px-2 py-0.5 rounded">ILLUSTRATIVE</span>
                    <span className="text-[10px] text-slate-500 font-mono">{ex.packet_id}</span>
                  </div>
                  <div className="text-white font-semibold">{ex.buyer_display}</div>
                  <div className="text-sm text-slate-500">
                    would be harmed by <span className="text-slate-400">{ex.seller_display}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold tracking-[1.5px] text-slate-400 mb-1">RECOURSE WOULD PAY</div>
                  <div className="text-2xl font-black tabular-nums text-slate-300">
                    ${ex.harm_amount_usdc.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-3 border-t border-white/5">
                <a href={ex.packet_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5" data-testid={`view-packet-${ex.packet_id}`}>
                    <FileText className="w-3.5 h-3.5 mr-2" />
                    View packet structure (JSON)
                  </Button>
                </a>
                <a href="/downloads/kojiru-slash-packet-000001.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10" data-testid={`view-pdf-${ex.packet_id}`}>
                    <Download className="w-3.5 h-3.5 mr-2" />
                    Download PDF
                  </Button>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* How verification will work */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-6">How verification will work</h2>
          <p className="text-slate-400 leading-relaxed mb-6">
            Once real slashes begin firing, every entry above will link to real transaction hashes and
            state changes. The verification path will be:
          </p>
          <ol className="space-y-4 text-slate-300">
            <li className="flex gap-4"><span className="text-rose-400 font-bold w-6 flex-shrink-0">1.</span><span>Open any packet. Every hash, wallet address, and block number will be a real on-chain value.</span></li>
            <li className="flex gap-4"><span className="text-rose-400 font-bold w-6 flex-shrink-0">2.</span><span>Paste the payment transaction hash into <a href="https://basescan.org" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">basescan.org</a>.</span></li>
            <li className="flex gap-4"><span className="text-rose-400 font-bold w-6 flex-shrink-0">3.</span><span>Confirm the amount, sender, and receiver match the packet.</span></li>
            <li className="flex gap-4"><span className="text-rose-400 font-bold w-6 flex-shrink-0">4.</span><span>Repeat with the proof-of-breach and slash transaction hashes. The slash and the proof share the same transaction &mdash; that is the point.</span></li>
            <li className="flex gap-4"><span className="text-rose-400 font-bold w-6 flex-shrink-0">5.</span><span>Read the contract source at <a href="https://basescan.org/address/0xaaae9ae4d52fe13384522e0ef4114b0a5f5d62d68#code" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:underline">the KojiruACSHook address</a>. Same code, verified on BaseScan.</span></li>
          </ol>
          <p className="text-sm text-slate-500 mt-8 italic">
            If a packet on this page ever cannot be independently verified against BaseScan, please email
            wayne@kojiru.com and we will correct it before anyone else reads.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
